import { useCallback, useState } from "react";
import { getAppDownloads, AppDownloadsResponse } from "../../../api";

interface FetchArgs {
    startDate?: Date | null;
    endDate?: Date | null;
}

interface UseDownloadStats {
    data: AppDownloadsResponse | null;
    loading: boolean;
    error: string | null;
    /** null statusCode 403 → caller not allowed to see downloads (gating). */
    forbidden: boolean;
    fetch: (args?: FetchArgs) => Promise<void>;
    reset: () => void;
}

export const useDownloadStats = (): UseDownloadStats => {
    const [data, setData] = useState<AppDownloadsResponse | null>(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [forbidden, setForbidden] = useState(false);

    const fetch = useCallback(async ({ startDate, endDate }: FetchArgs = {}) => {
        const token = localStorage.getItem("token");
        if (!token) {
            setError("missing-token");
            return;
        }
        setLoading(true);
        setError(null);
        setForbidden(false);
        try {
            const res = await getAppDownloads({
                token,
                startDate: startDate ?? null,
                endDate: endDate ?? null,
            });
            setData(res);
        } catch (e: any) {
            if (e?.statusCode === 403) {
                setForbidden(true);
                setData(null);
            } else {
                setError(e?.message || "unknown-error");
            }
        } finally {
            setLoading(false);
        }
    }, []);

    const reset = useCallback(() => {
        setData(null);
        setError(null);
        setForbidden(false);
    }, []);

    return { data, loading, error, forbidden, fetch, reset };
};
